import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Card,
  CardContent,
  Avatar,
  Stack,
  Divider,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  useMediaQuery,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  Inventory,
  TrendingUp,
  Assessment,
  Storage,
  FiberNew,
  Update,
  Add,
  Analytics,
  Category,
  ArrowForward,
  MoreVert,
  LocationOn,
  CalendarToday,
  Business,
  CategoryOutlined,
  Home,
  ViewInAr,
  Apps,
  Language,
  AutoAwesome,
} from "@mui/icons-material";
import { PageHeader, StatCard, LoadingSpinner } from "../components";
import { ItemForm } from "../components";
import { supabaseApi as api } from "../services/supabaseApi";
import { useItems } from "../hooks/useItems";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { CategoryFormData, MerchantFormData, Merchant } from "@catalog/types";

// Category Form Component
interface CategoryFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CategoryFormData) => void;
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState<CategoryFormData>({
    name: "",
    description: "",
  });

  const handleInputChange = (field: keyof CategoryFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({ name: "", description: "" });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile} // Full screen on mobile
    >
      <DialogTitle
        sx={{
          pb: { xs: 1, md: 2 }, // Reduced padding on mobile
          px: { xs: 2, md: 3 }, // Reduced horizontal padding on mobile
        }}
      >
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, md: 2 }}>
          <Category sx={{ fontSize: { xs: 20, md: 24 } }} />{" "}
          {/* Smaller icon on mobile */}
          <Typography
            variant={isMobile ? "h6" : "h6"}
            sx={{ fontSize: { xs: "1.125rem", md: "1.25rem" } }} // Smaller title on mobile
          >
            Add New Category
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 2, md: 3 }, // Reduced horizontal padding on mobile
          pt: { xs: 1, md: 2 }, // Reduced top padding on mobile
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ pt: { xs: 1, md: 2 } }}
        >
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {" "}
            {/* Reduced spacing on mobile */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                required
                placeholder="e.g., Furniture, Electronics, Decorations"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                multiline
                rows={isMobile ? 2 : 3} // Fewer rows on mobile
                placeholder="Optional description of this category..."
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: 2, md: 3 }, // Reduced padding on mobile
          gap: { xs: 1, md: 0 }, // Add gap on mobile
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text on mobile
            px: { xs: 2, md: 3 }, // Reduced padding on mobile
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.name.trim()}
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text on mobile
            px: { xs: 2, md: 3 }, // Reduced padding on mobile
          }}
        >
          Add Category
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Merchant Form Component
interface MerchantFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: MerchantFormData) => void;
}

const MerchantForm: React.FC<MerchantFormProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState<MerchantFormData>({
    name: "",
    url: "",
    website: "",
    image_url: "",
    is_active: true,
  });

  const [isAutoFetching, setIsAutoFetching] = useState(false);

  // Reset form data when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        name: "",
        url: "",
        website: "",
        image_url: "",
        is_active: true,
      });
    }
  }, [open]);

  const handleInputChange = (field: keyof MerchantFormData, value: any) => {
    setFormData((prev: MerchantFormData) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAutoFetch = async () => {
    if (!formData.url) return;

    setIsAutoFetching(true);
    try {
      // Extract domain name for merchant name if not provided
      const urlObj = new URL(
        formData.url.startsWith("http")
          ? formData.url
          : `https://${formData.url}`
      );
      const domain = urlObj.hostname.replace("www.", "");
      const merchantName = domain.split(".")[0].toUpperCase();

      // Auto-populate basic information
      setFormData((prev) => ({
        ...prev,
        name: prev.name || merchantName,
        website: urlObj.href,
        image_url: prev.image_url || `https://logo.clearbit.com/${domain}`,
      }));
    } catch (error) {
      console.error("Error auto-fetching merchant info:", error);
    } finally {
      setIsAutoFetching(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle
        sx={{
          pb: { xs: 1, md: 2 },
          px: { xs: 2, md: 3 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, md: 2 }}>
          <Business sx={{ fontSize: { xs: 20, md: 24 } }} />
          <Typography
            variant="h6"
            sx={{ fontSize: { xs: "1.125rem", md: "1.25rem" } }}
          >
            Add New Merchant
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 2, md: 3 },
          pt: { xs: 1, md: 2 },
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ pt: { xs: 1, md: 2 } }}
        >
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {/* URL Field with Auto-Fetch */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Merchant URL"
                value={formData.url}
                onChange={(e) => handleInputChange("url", e.target.value)}
                placeholder="https://www.ikea.com or ikea.com"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleAutoFetch}
                        disabled={!formData.url || isAutoFetching}
                        color="primary"
                        title="Auto-fetch merchant info"
                      >
                        {isAutoFetching ? (
                          <CircularProgress size={20} />
                        ) : (
                          <AutoAwesome />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Enter the merchant's website URL and click the magic icon to auto-fetch info"
              />
            </Grid>
            {/* Name Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Merchant Name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                required
                placeholder="e.g., IKEA, Amazon, Best Buy"
              />
            </Grid>
            {/* Website Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={(e) => handleInputChange("website", e.target.value)}
                placeholder="https://www.merchant.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Language />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {/* Image URL Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Logo URL"
                value={formData.image_url}
                onChange={(e) => handleInputChange("image_url", e.target.value)}
                placeholder="https://example.com/logo.png"
                helperText="Optional: URL to the merchant's logo image"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: 2, md: 3 },
          gap: { xs: 1, md: 0 },
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" },
            px: { xs: 2, md: 3 },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.name.trim()}
          sx={{
            fontSize: { xs: "0.875rem", md: "1rem" },
            px: { xs: 2, md: 3 },
          }}
        >
          Add Merchant
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DashboardPage: React.FC = () => {
  const [isItemFormOpen, setIsItemFormOpen] = useState(false);
  const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false);
  const [isMerchantFormOpen, setIsMerchantFormOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: items, isLoading: itemsLoading } = useItems();
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: api.dashboard.getStats,
  });

  const handleAddNewItem = () => {
    setIsItemFormOpen(true);
  };

  const handleAddMerchant = () => {
    setIsMerchantFormOpen(true);
  };

  const handleAddCategory = () => {
    setIsCategoryFormOpen(true);
  };

  const handleItemFormClose = () => {
    setIsItemFormOpen(false);
  };

  const handleItemFormSubmit = async (newItem: any) => {
    try {
      // Invalidate and refetch items and stats
      await queryClient.invalidateQueries({ queryKey: ["items"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });

      setSnackbar({
        open: true,
        message: `Successfully added "${newItem.name}" to inventory!`,
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to add item. Please try again.",
        severity: "error",
      });
    }
  };

  const handleCategoryFormClose = () => {
    setIsCategoryFormOpen(false);
  };

  const handleCategoryFormSubmit = async (categoryData: CategoryFormData) => {
    try {
      await api.categories.create(categoryData);

      // Invalidate and refetch categories and stats
      await queryClient.invalidateQueries({ queryKey: ["categories"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });

      setSnackbar({
        open: true,
        message: `Successfully created "${categoryData.name}" category!`,
        severity: "success",
      });

      setIsCategoryFormOpen(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to create category. Please try again.",
        severity: "error",
      });
    }
  };

  const handleMerchantFormClose = () => {
    setIsMerchantFormOpen(false);
  };

  const handleMerchantFormSubmit = async (merchantData: MerchantFormData) => {
    try {
      await api.merchants.create(merchantData);

      // Invalidate and refetch merchants and stats
      await queryClient.invalidateQueries({ queryKey: ["merchants"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });

      setSnackbar({
        open: true,
        message: `Successfully created "${merchantData.name}" merchant!`,
        severity: "success",
      });

      setIsMerchantFormOpen(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to create merchant. Please try again.",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (itemsLoading || statsLoading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  const recentItems = items?.slice(0, 6) || [];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "background.default",
      }}
    >
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: "text.primary",
              letterSpacing: "-0.02em",
            }}
          >
            Dashboard
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontWeight: 400 }}
          >
            Overview of your inventory and recent activity
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 1.5, sm: 3 }}>
          {" "}
          {/* Reduced spacing on mobile */}
          {/* Stats Cards */}
          <Grid item xs={6} sm={6} lg={3}>
            <Paper
              onClick={() => navigate("/inventory")}
              sx={{
                p: { xs: 1.5, sm: 3 }, // More compact padding on mobile
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 1px 3px rgba(0, 0, 0, 0.3)"
                    : "0 1px 3px rgba(0, 0, 0, 0.04)",
                backgroundColor: "background.paper",
                transition: "all 0.2s ease",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 4px 12px rgba(0, 0, 0, 0.4)"
                      : "0 4px 12px rgba(0, 0, 0, 0.08)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={{ xs: 1, sm: 2 }}
              >
                <Box
                  sx={{
                    width: { xs: 36, sm: 48 }, // Smaller icon container on mobile
                    height: { xs: 36, sm: 48 },
                    borderRadius: 1,
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ViewInAr
                    sx={{
                      color: "primary.main",
                      fontSize: { xs: 18, sm: 24 }, // Smaller icon on mobile
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant={isMobile ? "h5" : "h4"} // Smaller text on mobile
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      mb: { xs: 0.25, sm: 0.5 },
                      fontSize: { xs: "1.25rem", sm: "2.125rem" }, // More specific size control
                    }}
                  >
                    {stats?.totalItems || 0}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }} // Smaller label on mobile
                  >
                    Total Items
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={6} lg={3}>
            <Paper
              sx={{
                p: { xs: 1.5, sm: 3 }, // More compact padding on mobile
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 1px 3px rgba(0, 0, 0, 0.3)"
                    : "0 1px 3px rgba(0, 0, 0, 0.04)",
                backgroundColor: "background.paper",
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 4px 12px rgba(0, 0, 0, 0.4)"
                      : "0 4px 12px rgba(0, 0, 0, 0.08)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={{ xs: 1, sm: 2 }}
              >
                <Box
                  sx={{
                    width: { xs: 36, sm: 48 }, // Smaller icon container on mobile
                    height: { xs: 36, sm: 48 },
                    borderRadius: 1,
                    backgroundColor: "rgba(46, 125, 50, 0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TrendingUp
                    sx={{
                      color: "success.main",
                      fontSize: { xs: 18, sm: 24 }, // Smaller icon on mobile
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant={isMobile ? "h5" : "h4"} // Smaller text on mobile
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      mb: { xs: 0.25, sm: 0.5 },
                      fontSize: { xs: "1.25rem", sm: "2.125rem" }, // More specific size control
                    }}
                  >
                    €{stats?.totalValue?.toLocaleString() || 0}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }} // Smaller label on mobile
                  >
                    Total Value
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={6} lg={3}>
            <Paper
              onClick={() => navigate("/categories")}
              sx={{
                p: { xs: 1.5, sm: 3 }, // More compact padding on mobile
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 1px 3px rgba(0, 0, 0, 0.3)"
                    : "0 1px 3px rgba(0, 0, 0, 0.04)",
                backgroundColor: "background.paper",
                transition: "all 0.2s ease",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 4px 12px rgba(0, 0, 0, 0.4)"
                      : "0 4px 12px rgba(0, 0, 0, 0.08)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={{ xs: 1, sm: 2 }}
              >
                <Box
                  sx={{
                    width: { xs: 36, sm: 48 }, // Smaller icon container on mobile
                    height: { xs: 36, sm: 48 },
                    borderRadius: 1,
                    backgroundColor: "rgba(245, 124, 0, 0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Category
                    sx={{
                      color: "warning.main",
                      fontSize: { xs: 18, sm: 24 }, // Smaller icon on mobile
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant={isMobile ? "h5" : "h4"} // Smaller text on mobile
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      mb: { xs: 0.25, sm: 0.5 },
                      fontSize: { xs: "1.25rem", sm: "2.125rem" }, // More specific size control
                    }}
                  >
                    {stats?.totalCategories || 0}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }} // Smaller label on mobile
                  >
                    Categories
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={6} lg={3}>
            <Paper
              onClick={() => navigate("/merchants")}
              sx={{
                p: { xs: 1.5, sm: 3 }, // More compact padding on mobile
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 1px 3px rgba(0, 0, 0, 0.3)"
                    : "0 1px 3px rgba(0, 0, 0, 0.04)",
                backgroundColor: "background.paper",
                transition: "all 0.2s ease",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 4px 12px rgba(0, 0, 0, 0.4)"
                      : "0 4px 12px rgba(0, 0, 0, 0.08)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={{ xs: 1, sm: 2 }}
              >
                <Box
                  sx={{
                    width: { xs: 36, sm: 48 }, // Smaller icon container on mobile
                    height: { xs: 36, sm: 48 },
                    borderRadius: 1,
                    backgroundColor: "rgba(211, 47, 47, 0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Business
                    sx={{
                      color: "error.main",
                      fontSize: { xs: 18, sm: 24 }, // Smaller icon on mobile
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant={isMobile ? "h5" : "h4"} // Smaller text on mobile
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      mb: { xs: 0.25, sm: 0.5 },
                      fontSize: { xs: "1.25rem", sm: "2.125rem" }, // More specific size control
                    }}
                  >
                    {stats?.totalMerchants || 0}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }} // Smaller label on mobile
                  >
                    Merchants
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          {/* Recent Items */}
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              order: { xs: 2, md: 1 }, // Show after Quick Actions on mobile
            }}
          >
            <Paper
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 1px 3px rgba(0, 0, 0, 0.3)"
                    : "0 1px 3px rgba(0, 0, 0, 0.04)",
                backgroundColor: "background.paper",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={{ xs: 2, md: 3 }} // Reduced margin on mobile
              >
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"} // Smaller title on mobile
                  fontWeight={600}
                  sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
                >
                  Recent Items
                </Typography>
                <FiberNew
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: 20, md: 24 }, // Smaller icon on mobile
                  }}
                />
              </Stack>

              {recentItems.length > 0 ? (
                <Stack spacing={0}>
                  {recentItems.map((item: any, index: number) => (
                    <Box
                      key={item.id}
                      sx={{
                        py: { xs: 1.5, md: 2 }, // More compact on mobile
                        px: 0,
                        borderBottom:
                          index < recentItems.length - 1
                            ? "1px solid rgba(0, 0, 0, 0.06)"
                            : "none",
                        "&:hover": {
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.05)"
                              : "rgba(0, 0, 0, 0.02)",
                          cursor: "pointer",
                        },
                        borderRadius: 1,
                        transition: "background-color 0.2s ease",
                      }}
                      onClick={() => navigate("/inventory")}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={{ xs: 1.5, md: 2 }}
                      >
                        <Avatar
                          sx={{
                            width: { xs: 32, md: 40 }, // Smaller avatar on mobile
                            height: { xs: 32, md: 40 },
                            backgroundColor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.08)"
                                : "rgba(0, 0, 0, 0.04)",
                            color: "text.secondary",
                            fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text in avatar
                          }}
                        >
                          {item.name?.[0]?.toUpperCase() || "?"}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{
                              color: "text.primary",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: { xs: "0.875rem", md: "0.875rem" }, // Consistent smaller text
                            }}
                          >
                            {item.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              fontSize: { xs: "0.7rem", md: "0.75rem" }, // Smaller price on mobile
                            }}
                          >
                            €{item.purchase_price?.toLocaleString()}
                          </Typography>
                        </Box>
                        <Chip
                          label={item.status?.replace("_", " ")}
                          size="small"
                          sx={{
                            backgroundColor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.08)"
                                : "rgba(0, 0, 0, 0.04)",
                            color: "text.secondary",
                            fontSize: { xs: "0.65rem", md: "0.7rem" }, // Smaller chip text on mobile
                            height: { xs: 18, md: 20 }, // Smaller chip on mobile
                            textTransform: "capitalize",
                          }}
                        />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    color: "text.secondary",
                  }}
                >
                  <Typography variant="body2">No items yet</Typography>
                  <Typography variant="caption">
                    Add your first item to get started
                  </Typography>
                </Box>
              )}

              {recentItems.length > 0 && (
                <Button
                  fullWidth
                  endIcon={<ArrowForward />}
                  onClick={() => navigate("/inventory")}
                  sx={{
                    mt: { xs: 2, md: 3 }, // Reduced margin on mobile
                    py: { xs: 0.75, md: 1 }, // More compact on mobile
                    color: "text.secondary",
                    fontWeight: 500,
                    textTransform: "none",
                    fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text on mobile
                    "&:hover": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(25, 118, 210, 0.04)",
                    },
                  }}
                >
                  View All Items
                </Button>
              )}
            </Paper>
          </Grid>
          {/* Quick Actions */}
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              order: { xs: 1, md: 2 }, // Show before Recent Items on mobile
            }}
          >
            <Paper
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 1px 3px rgba(0, 0, 0, 0.3)"
                    : "0 1px 3px rgba(0, 0, 0, 0.04)",
                backgroundColor: "background.paper",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={{ xs: 2, md: 3 }} // Reduced margin on mobile
              >
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"} // Smaller title on mobile
                  fontWeight={600}
                  sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
                >
                  Quick Actions
                </Typography>
                <Analytics
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: 20, md: 24 }, // Smaller icon on mobile
                  }}
                />
              </Stack>

              <Grid container spacing={{ xs: 1.5, md: 2 }}>
                {" "}
                {/* Reduced spacing on mobile */}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Add />}
                    onClick={handleAddNewItem}
                    sx={{
                      py: { xs: 1, md: 1.5 }, // More compact on mobile
                      backgroundColor: "primary.main",
                      fontWeight: 500,
                      textTransform: "none",
                      borderRadius: 1,
                      fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text on mobile
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                    }}
                  >
                    Add New Item
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Business />}
                    onClick={handleAddMerchant}
                    sx={{
                      py: { xs: 1, md: 1.5 }, // More compact on mobile
                      fontWeight: 500,
                      textTransform: "none",
                      borderRadius: 1,
                      fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text on mobile
                      borderColor: "rgba(0, 0, 0, 0.12)",
                      "&:hover": {
                        borderColor: "primary.main",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                  >
                    Add Merchant
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Category />}
                    onClick={handleAddCategory}
                    sx={{
                      py: { xs: 1, md: 1.5 }, // More compact on mobile
                      fontWeight: 500,
                      textTransform: "none",
                      borderRadius: 1,
                      fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text on mobile
                      borderColor: "rgba(0, 0, 0, 0.12)",
                      "&:hover": {
                        borderColor: "primary.main",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                  >
                    Add Category
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<ViewInAr />}
                    onClick={() => navigate("/inventory")}
                    sx={{
                      py: { xs: 1, md: 1.5 }, // More compact on mobile
                      fontWeight: 500,
                      textTransform: "none",
                      borderRadius: 1,
                      fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text on mobile
                      borderColor: "rgba(0, 0, 0, 0.12)",
                      "&:hover": {
                        borderColor: "primary.main",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(25, 118, 210, 0.04)",
                      },
                    }}
                  >
                    View Inventory
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Item Form Modal */}
        <ItemForm
          open={isItemFormOpen}
          onClose={handleItemFormClose}
          onSubmit={handleItemFormSubmit}
        />

        {/* Category Form Modal */}
        <CategoryForm
          open={isCategoryFormOpen}
          onClose={handleCategoryFormClose}
          onSubmit={handleCategoryFormSubmit}
        />

        {/* Merchant Form Modal */}
        <MerchantForm
          open={isMerchantFormOpen}
          onClose={handleMerchantFormClose}
          onSubmit={handleMerchantFormSubmit}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default DashboardPage;
